import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import Seo, { breadcrumb } from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { CoveButton } from "@/components/CoveButton";
import { ArchImage } from "@/components/ArchImage";
import { Reveal, FloatingBlob } from "@/components/motion";
import {
  IMAGES,
  POST_SESSION_INTRO,
  POST_SESSION_LEAD,
  POST_SESSION,
  POST_SESSION_NOTE,
  WHY_HAPPENS_INTRO,
  WHY_HAPPENS,
  WHY_HAPPENS_NOTE,
  SUPPORT_HEALING,
  OTHER_THERAPIES_NOTE,
  HEALING_JOURNEY,
} from "@/data/content";

const JOURNEY = [
  {
    title: "Booking an appointment",
    body:
      "Book directly through the online calendar, or send Laura a message with any questions before you reserve a time, she'll reply personally.",
  },
  {
    title: "Completing intake forms",
    body:
      "You'll find the client intake form on this website, please complete it before your first visit so Laura can understand how best to support you.",
  },
  {
    title: "Arriving at the practice",
    body:
      "Cranio Cove is located within The Hearth by Well Wombn, 200 S. Broadway, Building 1, Suite 100, Tarrytown, NY 10591. Arrive a few minutes early to settle.",
  },
  {
    title: "Comfort, goals & consent",
    body:
      "We begin with an unhurried conversation about how you're feeling and what you'd like from the session. Consent is discussed openly and revisited throughout.",
  },
  {
    title: "What to wear",
    body:
      "Wear comfortable, loose clothing. You remain fully clothed for the entire session, there is nothing to remove.",
  },
  {
    title: "During the session",
    body:
      "You rest on a padded table while Laura uses light contact to support your nervous system. You're welcome to speak, rest quietly, or simply drift.",
  },
  {
    title: "Session length",
    body:
      "Sessions typically run 30–90 minutes. Your first visit is a little longer to allow time for conversation and settling in.",
  },
  {
    title: "Afterward",
    body:
      "Many people feel calm, grounded, or gently dreamy. You're encouraged to keep the rest of your day soft and to drink plenty of water.",
  },
  {
    title: "Rescheduling & cancellation",
    body:
      "Life happens, please give at least 24 hours' notice to reschedule or cancel. Full policy details are confirmed at the time of booking.",
  },
];

const Timeline = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 30%", "end 70%"] });
  const height = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  return (
    <div ref={ref} className="relative mx-auto max-w-4xl">
      <div className="absolute left-[22px] md:left-1/2 top-2 bottom-2 w-px bg-cove-slate/15 md:-translate-x-1/2" aria-hidden="true" />
      <motion.div style={{ height }} className="absolute left-[22px] md:left-1/2 top-2 w-px bg-cove-rose md:-translate-x-1/2 origin-top" aria-hidden="true" />

      <ol className="space-y-14 md:space-y-24">
        {JOURNEY.map((step, i) => (
          <li key={i} className="relative">
            <Reveal>
              <div className={`md:grid md:grid-cols-2 md:gap-16 items-center ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}>
                <div className={`pl-16 md:pl-0 [direction:ltr] ${i % 2 === 1 ? "md:text-right" : ""}`}>
                  <h3 className="font-serif text-3xl text-cove-slate">{step.title}</h3>
                  <p className="mt-4 text-cove-olive leading-relaxed max-w-md md:inline-block">{step.body}</p>
                </div>
                <div className="hidden md:block" />
              </div>
            </Reveal>
            <span className="absolute left-[15px] md:left-1/2 top-2 h-3.5 w-3.5 rounded-full bg-cove-rose md:-translate-x-1/2 ring-4 ring-cove-cream" aria-hidden="true" />
          </li>
        ))}
      </ol>
    </div>
  );
};

export default function WhatToExpect() {
  return (
    <>
      <Seo
        title="What to Expect"
        description="Your time in the cove, step by step, from booking and intake to the session itself and afterward. Clients remain fully clothed, with consent encouraged throughout."
        path="/what-to-expect"
      />
      <PageHero
        overline="What to expect"
        titleLines={["Your time", "in the cove"]}
        intro="A clear, gentle walk-through of the whole experience, so you know exactly what to expect before, during, and after your session."
        breadcrumb={breadcrumb([{ name: "Home", path: "/" }, { name: "What to Expect", path: "/what-to-expect" }])}
        heroImage={{ src: IMAGES.coveExpect, alt: "A calm lake at dusk with a pink and lavender sunset and a wooden dock" }}
      />

      <section className="relative px-6 md:px-12 py-16 md:py-24 overflow-hidden" data-testid="expect-timeline">
        <FloatingBlob color="blush" size={360} className="top-40 -left-24" delay={0.2} />
        <FloatingBlob color="sage" size={320} className="bottom-40 -right-20" delay={0.5} drift={30} />
        <div className="relative">
          <Timeline />
        </div>
      </section>

      {/* Post-session experiences */}
      <section
        className="relative px-6 md:px-12 py-20 md:py-28 bg-gradient-to-br from-cove-restorative via-cove-blossom/40 to-cove-mutedblue/25 overflow-hidden"
        data-testid="post-session"
      >
        <FloatingBlob color="rose" size={340} className="top-10 -right-24" delay={0.2} />
        <div className="relative mx-auto max-w-[1200px]">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-4">After a session</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-cove-slate max-w-3xl">
              Healing is <span className="italic text-cove-rose">not linear</span>
            </h2>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-cove-olive">
              {POST_SESSION_INTRO}
            </p>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-cove-olive">
              {POST_SESSION_LEAD}
            </p>
          </Reveal>
          <ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {POST_SESSION.map((line, i) => {
              const tints = [
                "bg-cove-blossom/60",
                "bg-cove-mutedblue/20",
                "bg-cove-restorative",
                "bg-cove-sage/30",
                "bg-cove-bluestone/20",
              ];
              return (
                <li key={i}>
                  <Reveal delay={i * 0.06}>
                    <div className={`h-full rounded-[1.25rem] ${tints[i % tints.length]} border border-cove-slate/10 p-5 flex items-start gap-3`}>
                      <Check size={16} className="mt-1 text-cove-groundedolive shrink-0" aria-hidden="true" />
                      <p className="text-cove-olive leading-relaxed">{line}</p>
                    </div>
                  </Reveal>
                </li>
              );
            })}
          </ul>
          <Reveal delay={0.4}>
            <p className="mt-8 max-w-2xl font-serif italic text-xl text-cove-slate">
              {POST_SESSION_NOTE}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Why This Happens */}
      <section
        className="px-6 md:px-12 py-16 md:py-24 bg-gradient-to-b from-cove-mutedblue/25 via-cove-restorative to-cove-sage/25"
        data-testid="why-happens"
      >
        <div className="mx-auto max-w-[1200px] grid lg:grid-cols-2 gap-10 lg:gap-20 items-start">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-4">Why this happens</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[0.95] text-cove-slate">
              Your body is <span className="italic text-cove-sage">recalibrating</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-cove-olive">
              {WHY_HAPPENS_INTRO}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="space-y-4">
              {WHY_HAPPENS.map((line, i) => (
                <li
                  key={i}
                  className="rounded-[1.25rem] bg-cove-cream border border-cove-slate/10 p-5 flex items-start gap-3"
                >
                  <Sparkles size={20} className="text-cove-gold shrink-0 mt-1" aria-hidden="true" />
                  <p className="text-cove-olive leading-relaxed pt-0.5">{line}</p>
                </li>
              ))}
            </ul>
            <p className="mt-6 font-serif italic text-xl leading-snug text-cove-slate">
              {WHY_HAPPENS_NOTE}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Support Your Healing */}
      <section
        className="relative px-6 md:px-12 py-20 md:py-28 bg-gradient-to-tr from-cove-sage/25 via-cove-restorative to-cove-blossom/50 overflow-hidden"
        data-testid="support-healing"
      >
        <FloatingBlob color="blush" size={340} className="-top-10 -left-24" delay={0.2} />
        <div className="relative mx-auto max-w-[1400px]">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-4">Support your healing</p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] text-cove-slate max-w-2xl">
              Care for yourself <span className="italic text-cove-sage">afterward</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {SUPPORT_HEALING.map((item, i) => {
              const tints = [
                "bg-cove-blossom/70",
                "bg-cove-mutedblue/25",
                "bg-cove-restorative",
                "bg-cove-sage/35",
                "bg-cove-bluestone/25",
              ];
              return (
                <Reveal key={i} delay={i * 0.06}>
                  <div className={`h-full rounded-[1.5rem] ${tints[i % tints.length]} border border-cove-slate/10 p-6`}>
                    <Sparkles size={24} className="text-cove-gold" aria-hidden="true" />
                    <h3 className="mt-3 font-serif text-xl text-cove-slate">{item.title}</h3>
                    <p className="mt-3 text-sm text-cove-olive leading-relaxed">{item.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* A Note on Other Therapies */}
      <section className="px-6 md:px-12 py-16 md:py-20" data-testid="other-therapies">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <p className="text-xs tracking-[0.25em] uppercase text-cove-rose mb-4 text-center">
              A note on other therapies
            </p>
            <p className="mx-auto max-w-2xl text-center text-lg leading-relaxed text-cove-olive">
              {OTHER_THERAPIES_NOTE}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Your Healing is a Journey, Laura's closing note */}
      <section
        className="px-6 md:px-12 pb-20 md:pb-28"
        data-testid="healing-journey"
      >
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <div className="rounded-[2rem] md:rounded-[3rem] bg-cove-nightblue text-cove-cream px-8 md:px-14 py-14 md:py-20 grain text-center">
              <h2 className="font-serif text-4xl md:text-5xl leading-tight">
                {HEALING_JOURNEY.heading}
              </h2>
              <div className="mt-8 space-y-5 text-cove-blue leading-relaxed">
                {HEALING_JOURNEY.body.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <p className="mt-10 font-script text-4xl text-cove-blush">{HEALING_JOURNEY.signoff}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-28 md:pb-40" data-testid="expect-cta">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <div className="rounded-[2rem] md:rounded-[3rem] bg-cove-sage/25 px-8 md:px-16 py-16 md:py-20 text-center">
              <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] text-cove-slate">
                Feel ready to begin?
              </h2>
              <p className="mt-6 max-w-xl mx-auto text-cove-olive leading-relaxed">
                Book your first session, or explore services and pricing to find the option that
                feels right for you.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                <CoveButton calendly variant="primary" testId="expect-book-button">
                  Book a Session
                </CoveButton>
                <CoveButton to="/services" variant="secondary" testId="expect-services-button">
                  Services & Pricing
                </CoveButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
